import {
  classifyBetterAuthRoute,
  resolveBetterAuthActivityResult,
  type BetterAuthActivityIntent,
} from "./auth-activity-coverage.js";

type AuthActivityParams = {
  action: string;
  outcome: "success" | "failure" | "denied";
  actorType?: string;
  actorUserId?: string | null;
  actorSessionToken?: string | null;
  reasonCode?: string | null;
  metadata?: Record<string, unknown>;
  authAttempt?: {
    attemptedEmail?: string | null;
    matchedUserId?: string | null;
    provider?: string | null;
  };
  httpStatus?: number | null;
};

type SessionSnapshot = {
  session?: { user?: { id?: string | null } | null } | null;
  sessionToken?: string | null;
} | null;

type AuditedDelegateParams = {
  request: Request;
  audit?: boolean;
  delegate: (request: Request) => Promise<Response>;
  resolveSessionBeforeDelegate: () => Promise<SessionSnapshot>;
  recordActivity: (params: AuthActivityParams) => Promise<void>;
};

async function readUserIdFromResponse(response: Response): Promise<string | null> {
  const payload = (await response
    .clone()
    .json()
    .catch(() => null)) as { user?: { id?: unknown } } | null;
  const userId = payload?.user?.id;
  return typeof userId === "string" && userId.trim() ? userId : null;
}

async function readAuthAttemptFromRequest(request: Request): Promise<AuthActivityParams["authAttempt"]> {
  if (!request.headers.get("content-type")?.toLowerCase().includes("application/json")) return undefined;
  const payload = (await request
    .clone()
    .json()
    .catch(() => null)) as { email?: unknown; provider?: unknown } | null;
  const email = typeof payload?.email === "string" ? payload.email : null;
  const provider = typeof payload?.provider === "string" ? payload.provider : "email";
  return email ? { attemptedEmail: email, provider } : undefined;
}

async function recordDelegateActivity(
  params: Pick<AuditedDelegateParams, "recordActivity"> & {
    intent: BetterAuthActivityIntent;
    response: Response;
    sessionBeforeDelegate: SessionSnapshot;
    authAttempt: AuthActivityParams["authAttempt"];
  },
): Promise<void> {
  const result = resolveBetterAuthActivityResult(params.intent, params.response.status);
  const actorUserId =
    params.sessionBeforeDelegate?.session?.user?.id ??
    (result.outcome === "success" && params.intent.readUserFromResponse
      ? await readUserIdFromResponse(params.response)
      : null);
  const actorSessionToken = params.sessionBeforeDelegate?.sessionToken ?? params.response.headers.get("set-auth-token");
  await params.recordActivity({
    action: result.action,
    outcome: result.outcome,
    actorType: actorUserId ? "user" : "anonymous",
    actorUserId,
    actorSessionToken,
    reasonCode: result.reasonCode,
    metadata: { source: params.intent.source },
    authAttempt: {
      ...params.authAttempt,
      matchedUserId: actorUserId,
    },
    httpStatus: params.response.status,
  });
}

export async function handleAuditedBetterAuthDelegate(params: AuditedDelegateParams): Promise<Response> {
  const intent =
    params.audit === false
      ? null
      : classifyBetterAuthRoute(params.request.method, new URL(params.request.url).pathname);
  const authAttempt = intent
    ? await readAuthAttemptFromRequest(params.request).catch(() => undefined)
    : undefined;
  const sessionBeforeDelegate = intent?.sessionBeforeDelegate
    ? await params.resolveSessionBeforeDelegate().catch(() => null)
    : null;
  const response = await params.delegate(params.request);
  if (intent) await recordDelegateActivity({ ...params, intent, response, sessionBeforeDelegate, authAttempt });
  return response;
}
